import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  Box,
  Button,
  Text,
  HStack,
  SimpleGrid,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
  useColorModeValue,
} from "@chakra-ui/react";
import { CalendarIcon } from "@chakra-ui/icons";
import { formatDateToDDMONTHNAMEYYYY } from "utils/common";

const FilterCalendar = ({
  startDate,
  endDate,
  setStartDate = () => {},
  setEndDate = () => {},
  onChange,
  onApply = () => {},
}) => {
  const [dateRange, setDateRange] = useState([startDate, endDate]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const today = new Date();

  useEffect(() => {
    if (selectedPreset) {
      setDateRange(selectedPreset);
    }
  }, [selectedPreset]);

  const applyFilter = (onClose) => {
    setStartDate(dateRange[0]);
    setEndDate(dateRange[1]);

    onChange({
      target: {
        name: "dateRange",
        value: { startDate: dateRange[0], endDate: dateRange[1] },
      },
    });

    onApply();
    onClose();
  };

  const resetDateRange = () => {
    setSelectedPreset(null);
    setDateRange([null, null]);
  };

  const onDateChange = (value) => {
    if (value?.[1] && value[1] > today) value[1] = today;
    setSelectedPreset(null);
    setDateRange(value);
  };

  const calendarBg = useColorModeValue("white", "gray.700");
  const calendarBorderColor = useColorModeValue("gray.200", "gray.600");

  return (
    <Popover placement="auto" strategy="fixed" closeOnBlur={false} isLazy>
      {({ onClose }) => (
        <>
          <PopoverTrigger>
            <Button
              leftIcon={<CalendarIcon />}
              bg="blue.500"
              color="white"
              _hover={{ bg: "blue.600" }}
              size="sm"
            >
              Select Date Range
            </Button>
          </PopoverTrigger>

          <PopoverContent
            zIndex={20000}
            maxW={{ base: "95vw", md: "520px" }}
            w="100%"
          >
            <PopoverArrow />
            <PopoverCloseButton />

            <PopoverBody>
              <Box
                display="flex"
                flexDir={{ base: "column", md: "row" }}
                gap={4}
              >
                {/* Presets */}
                <SimpleGrid
                  columns={{ base: 3, md: 1 }}
                  spacing={2}
                  minW={{ md: "120px" }}
                >
                  <Button
                    size="xs"
                    onClick={() => setSelectedPreset([today, today])}
                  >
                    Today
                  </Button>
                  <Button
                    size="xs"
                    onClick={() => {
                      const d = new Date();
                      d.setDate(today.getDate() - 1);
                      setSelectedPreset([d, d]);
                    }}
                  >
                    Yesterday
                  </Button>
                  <Button
                    size="xs"
                    onClick={() => {
                      const d = new Date();
                      d.setDate(today.getDate() - 7);
                      setSelectedPreset([d, today]);
                    }}
                  >
                    Last 7 Days
                  </Button>
                  <Button
                    size="xs"
                    onClick={() => {
                      const d = new Date();
                      d.setDate(today.getDate() - 30);
                      setSelectedPreset([d, today]);
                    }}
                  >
                    Last 30 Days
                  </Button>
                  <Button size="xs" colorScheme="red" onClick={resetDateRange}>
                    Reset
                  </Button>
                </SimpleGrid>

                {/* Calendar */}
                <Box flex="1" minW={0}>
                  <Calendar
                    selectRange
                    onChange={onDateChange}
                    value={dateRange}
                    tileDisabled={({ date }) => date > today}
                    className="react-calendar"
                  />

                  <HStack mt={3} spacing={4} justify="space-between">
                    <Text fontSize="xs">
                      <b>Start:</b>{" "}
                      {dateRange[0]
                        ? formatDateToDDMONTHNAMEYYYY(dateRange[0])
                        : "N/A"}
                    </Text>
                    <Text fontSize="xs">
                      <b>End:</b>{" "}
                      {dateRange[1]
                        ? formatDateToDDMONTHNAMEYYYY(dateRange[1])
                        : "N/A"}
                    </Text>
                  </HStack>

                  <Button
                    mt={3}
                    size="sm"
                    w="100%"
                    bg="blue.500"
                    color="white"
                    _hover={{ bg: "blue.600" }}
                    onClick={() => applyFilter(onClose)}
                  >
                    Apply Filter
                  </Button>
                </Box>
              </Box>
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
};

export default FilterCalendar;
